import { Segmented } from "@/components/ui/segmented";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { usePermission } from "@/hooks/usePermission";
import { PageHeader } from "@/components/ui/page-header";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import NewPatientModal from "@/components/Patient/NewPatientModal";
import {
  Users, Search, Phone, AlertTriangle,
  UserPlus, ChevronLeft, ChevronRight,
  ArrowUp, ArrowDown, ArrowUpDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getPatients } from '@/lib/api';
import { displayAge } from '@/lib/patientAge';
import { parseAllergies } from '@/lib/allergyCheck';

interface Patient {
  id: string;
  name: string;
  age?: number;
  date_of_birth?: string;
  gender?: string;
  phone?: string;
  mrn?: string;
  allergies?: string | null;
  last_visit_date?: string;
  last_prescription_at?: string | null;   // latest prescription (list endpoint)
  prescription_count?: number;
  created_at: string;
  updated_at: string;
}

function timeAgo(dateStr?: string): string | null {
  if (!dateStr) return null;
  try {
    const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7)  return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  } catch { return null; }
}

type SortField = 'name' | 'created_at';

// One grid for the header and rows from md up, so columns always line up
const ROW_GRID = "lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_8rem_7rem_9rem] lg:gap-4";

export default function PatientManagementPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { document.title = "Patients | OneCheckUp"; }, []);

  const [patients, setPatients]         = useState<Patient[]>([]);
  const [searchTerm, setSearchTerm]     = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [sortField, setSortField]       = useState<SortField>('created_at');
  const [sortOrder, setSortOrder]       = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading]           = useState(false);
  const [showAddPatient, setShowAddPatient] = useState(false);
  const { can } = usePermission();
  // Row → side panel (doctors); front desk goes straight to its patient page
  const [currentPage, setCurrentPage]   = useState(1);
  const [totalItems, setTotalItems]     = useState(0);
  const itemsPerPage = 20;

  const fetchPatients = useCallback(async () => {
    if (!currentUser?.clinic_id) return;
    setLoading(true);
    try {
      const result: { patients: Patient[]; total: number } = await getPatients({
        clinic_id: currentUser.clinic_id,
        search: searchTerm,
        gender: genderFilter,
        sortField,
        sortOrder,
        page: currentPage,
        pageSize: itemsPerPage,
      });
      setPatients(result.patients);
      setTotalItems(result.total);
    } catch {
      setPatients([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.clinic_id, searchTerm, genderFilter, sortField, sortOrder, currentPage]);

  useEffect(() => { fetchPatients(); }, [fetchPatients]);
  useEffect(() => { setCurrentPage(1); }, [searchTerm, genderFilter, sortField, sortOrder]);

  // Refresh when a patient is created from anywhere (e.g. GlobalTopBar)
  useEffect(() => {
    const handler = () => fetchPatients();
    window.addEventListener('patient:created', handler);
    return () => window.removeEventListener('patient:created', handler);
  }, [fetchPatients]);

  useEffect(() => {
    if (location.state?.focusSearch) {
      setTimeout(() => {
        const el = document.getElementById('patient-search') as HTMLInputElement;
        el?.focus(); el?.select();
      }, 100);
    }
  }, [location.state]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem  = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem    = Math.min(currentPage * itemsPerPage, totalItems);

  const isFrontDesk = location.pathname.startsWith('/frontdesk');
  const getPatientPath = (id: string) =>
    isFrontDesk ? `/frontdesk/patients/${id}` : `/patients/${id}`;

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortOrder(o => o === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortOrder('asc'); }
  };


  return (
    <div className="h-full flex flex-col bg-background">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 px-4 sm:px-6 pt-4 pb-3 border-b border-border space-y-3">

        <PageHeader
          icon={Users}
          title="Patients"
          subtitle={totalItems > 0 && !loading ? `${totalItems} patient${totalItems !== 1 ? 's' : ''}` : undefined}
        />

        <div className="flex items-center gap-2 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <Input
              id="patient-search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search by name, phone, or MRN…"
              className="pl-8 h-8 text-sm pr-8"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-base leading-none"
              >
                ×
              </button>
            )}
          </div>

          {/* Gender + Sort — unified pill group */}
          <Segmented
            aria-label="Gender"
            options={[{ value: 'all', label: 'All' }, { value: 'Male', label: 'Male' }, { value: 'Female', label: 'Female' }]}
            value={genderFilter || 'all'}
            onChange={g => setGenderFilter(g === 'all' ? '' : g)}
          />

          {/* Clicking the active sort flips its direction */}
          <Segmented<SortField>
            aria-label="Sort"
            options={([['created_at', 'Recent'], ['name', 'Name']] as const).map(([field, label]) => {
              const Icon = sortField === field ? (sortOrder === 'desc' ? ArrowDown : ArrowUp) : ArrowUpDown;
              return { value: field, label: <>{label} <Icon className="w-3 h-3" /></> };
            })}
            value={sortField}
            onChange={toggleSort}
          />
        </div>
      </div>

      {/* ── List ───────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-2">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-xs text-muted-foreground">Loading…</p>
          </div>

        ) : patients.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-center px-6">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
              <Users className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                {searchTerm ? `No results for "${searchTerm}"` : 'No patients yet'}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {searchTerm ? 'Try a different name, phone, or MRN' : 'Register your first patient to get started'}
              </p>
            </div>
            {!searchTerm && can('patients:write') && (
              <Button size="sm" onClick={() => setShowAddPatient(true)} className="mt-1 gap-1.5">
                <UserPlus className="w-3.5 h-3.5" /> Add First Patient
              </Button>
            )}
          </div>

        ) : (
          <div role="table" aria-label="Patients">
            <div role="row" className={cn(ROW_GRID, "hidden lg:grid h-8 px-4 sm:px-6 border-b border-border bg-muted/40 text-2xs font-medium uppercase tracking-wide text-muted-foreground")}>
              <span role="columnheader">Patient</span>
              <span role="columnheader">Allergies</span>
              <span role="columnheader">Phone</span>
              <span role="columnheader">MRN</span>
              <span role="columnheader" className="text-right">Last visit</span>
            </div>
            {patients.map((patient) => {
              const initials    = patient.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
              // Newest of the queue's last visit and the latest prescription
              const lastSeen = [patient.last_visit_date, patient.last_prescription_at ?? undefined]
                .filter((d): d is string => !!d)
                .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
              const lastVisit   = timeAgo(lastSeen);
              const visits      = Number(patient.prescription_count ?? 0);
              const allergies   = parseAllergies(patient.allergies);
              const demographics = [displayAge(patient.age, patient.date_of_birth), patient.gender].filter(Boolean).join(' · ');
              const open = () => navigate(getPatientPath(patient.id));

              return (
                <div
                  key={patient.id}
                  role="row"
                  tabIndex={0}
                  onClick={open}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
                  className={cn(
                    ROW_GRID,
                    "flex items-center gap-3 px-4 sm:px-6 py-2 lg:py-0 lg:h-10 cursor-pointer transition-colors border-b border-divider",
                    "bg-card hover:bg-muted/50 focus-visible:outline-none focus-visible:bg-muted/60"
                  )}
                >
                  {/* Patient: avatar, name, age · sex (phone and allergy fold in below lg) */}
                  <div role="cell" className="flex items-center gap-2.5 min-w-0 flex-1">
                    <span aria-hidden className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-2xs font-semibold flex-shrink-0 select-none">
                      {initials}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-baseline gap-2 min-w-0">
                        <span className="text-sm font-medium text-foreground truncate">{patient.name}</span>
                        {demographics && <span className="text-xs text-muted-foreground flex-shrink-0">{demographics}</span>}
                      </div>
                      <div className="lg:hidden flex items-center gap-x-3 flex-wrap text-xs text-muted-foreground">
                        {patient.phone && <span className="flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{patient.phone}</span>}
                        {allergies.length > 0 && (
                          <span className="flex items-center gap-1 font-medium text-destructive min-w-0">
                            <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">Allergy: {allergies.join(', ')}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div role="cell" className="hidden lg:block min-w-0">
                    {allergies.length > 0 ? (
                      <span className="inline-flex items-center gap-1 max-w-full text-xs font-medium text-destructive" title={`Allergies: ${allergies.join(', ')}`}>
                        <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{allergies.join(', ')}</span>
                      </span>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </div>

                  <div role="cell" className="hidden lg:block text-xs text-muted-foreground tabular-nums truncate">
                    {patient.phone || '—'}
                  </div>

                  <div role="cell" className="hidden sm:block text-xs font-mono text-foreground/80 truncate">
                    {patient.mrn || <span className="font-sans text-muted-foreground">—</span>}
                  </div>

                  <div role="cell" className="hidden lg:block text-right text-xs tabular-nums whitespace-nowrap">
                    {lastVisit
                      ? <><span className="text-foreground">{lastVisit}</span>{visits > 0 && <span className="text-muted-foreground"> · {visits} visit{visits === 1 ? '' : 's'}</span>}</>
                      : <span className="text-muted-foreground">—</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Pagination ─────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="flex-shrink-0 flex items-center justify-between px-4 sm:px-6 py-2.5 border-t border-border bg-background">
          <span className="text-xs text-muted-foreground tabular-nums">
            {startItem}–{endItem} of {totalItems}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1.5 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs px-2 tabular-nums text-muted-foreground">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-1.5 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}


      {showAddPatient && (
        <NewPatientModal
          isOpen={showAddPatient}
          onClose={() => setShowAddPatient(false)}
          onPatientCreated={() => { fetchPatients(); setShowAddPatient(false); }}
        />
      )}
    </div>
  );
}
